import { useCallback, useEffect, useMemo, useRef, useState, type MutableRefObject, type RefObject } from 'react'
import { useStore } from 'jotai'
import { Maximize2, X } from 'lucide-react'
import { Button, Popover, Tooltip, useKeyboardFinalFocus } from '@highlight/ui'
import { oppositeSide } from '@shared/sidepanel/side'
import { useSidepanelState } from '@renderer/overlay/atoms'
import { useClaimHostedViewOpen, useSidepanelSide } from '../../atoms'
import { isViewPopoutPointer, openViewWindow } from '../../open-view-window'
import { canPopOutView } from '../../surface'
import { isViewId, normalizeViewContext, type ViewId } from '../../view-definitions'
import { topEntry } from '../../view-history'
import { useViewActions, useViewNavigation, type OpenViewFunction, type SidepanelView } from '../../view-atoms'
import { ViewContext } from '../../view-context'
import { viewChrome } from '../../view-registry'
import { ViewSheetLayer } from '../../view-sheet-layer'
import { createViewScope, ViewScopeContext, type ViewScope } from '../../view-state-atoms'
import { SidepanelFloatingBar } from '../sidepanel/nested/floating-bar'
import { ViewChromeNav } from '../sidepanel/view-chrome-nav'
import { PopoutIcon, POPOUT_LABEL } from '../cell-hover-buttons'

// The one cast, as in `useOpenViewInSidepanel`: TS won't feed a
// conditional-tuple rest back into the generic signature it was declared from.
type LooseOpenView = (id: ViewId, context?: unknown, options?: unknown) => void

/**
 * The open popover's close, through the View's leave guard, for the row's
 * second press. `then` runs only once the View has actually gone — a guard
 * that needs confirming defers it, and a cancelled confirmation drops it.
 */
type LeaveRequest = (then?: () => void) => void
type CloseRequest = MutableRefObject<LeaveRequest | null>

/**
 * The closes of the View popovers open right now, as `row-step` keeps its
 * listeners. The panel hosts one View at a time — `hostedViewOpenAtom` is a
 * single slot — but every row owns its own popover state, so opening one has
 * to ask the rest to leave rather than quietly stacking on them.
 */
const openHostedViews = new Set<LeaveRequest>()

/**
 * Ask every open hosted View to leave, through its own guard, and run `then`
 * once they all have. A guard that needs confirming answers later, and a
 * cancelled confirmation never answers at all — so whatever was waiting on
 * the panel simply doesn't happen, and the View the user chose to stay on
 * stays.
 */
export function closeOpenHostedViews(then: () => void): void {
  const leaving = [...openHostedViews]
  if (leaving.length === 0) {
    then()
    return
  }
  let remaining = leaving.length
  for (const leave of leaving) {
    leave(() => {
      remaining -= 1
      if (remaining === 0) then()
    })
  }
}

/**
 * A row's View opened in a popover beside the panel instead of in it. Its
 * `openView` takes the panel's arguments, so a row swaps it in for the panel's
 * without changing how it builds the View. Popping out still opens a window.
 * Opening it again while it's open closes it, as a second press on the row.
 */
export function useViewPopover() {
  const { openView } = useViewActions()
  const [view, setView] = useState<SidepanelView | null>(null)
  const closeRequest: CloseRequest = useRef(null)
  const close = useCallback(() => setView(null), [])
  // The mounted `ViewPopover` swaps in its guarded close; until then there is
  // nothing to guard, so leaving is immediate.
  const requestClose = useCallback<LeaveRequest>(
    (then) => {
      const guarded = closeRequest.current
      if (guarded) {
        guarded(then)
        return
      }
      close()
      then?.()
    },
    [close],
  )

  useEffect(() => {
    if (!view) return
    openHostedViews.add(requestClose)
    return () => {
      openHostedViews.delete(requestClose)
    }
  }, [view, requestClose])

  const openInPopover = useCallback<OpenViewFunction>(
    (...args) => {
      const [id, context, options] = args
      const pointerEvent = options?.pointerEvent
      if (canPopOutView && (options?.popout || (pointerEvent && isViewPopoutPointer(pointerEvent)))) {
        ;(openView as LooseOpenView)(id, context, options)
        return
      }
      if (view) {
        requestClose()
        return
      }
      // Another row's View leaves through its own guard, as a second press on
      // that row would take it — and this one waits, because that guard may be
      // asking the user first. Cancelling there leaves the one they're on up
      // rather than stacking a second beside it.
      closeOpenHostedViews(() =>
        setView({ id, context: normalizeViewContext(id, context), sourceLabel: options?.sourceLabel }),
      )
    },
    [openView, view, requestClose],
  )

  return useMemo(() => ({ view, openView: openInPopover, close, closeRequest }), [view, openInPopover, close])
}

interface ViewPopoverProps {
  /** The View to show, or null while closed. */
  view: SidepanelView | null
  anchor: RefObject<Element>
  onClose: () => void
  /** From `useViewPopover`, so a second press on the row closes it the way Escape does. */
  closeRequest?: CloseRequest
}

/**
 * Hosts a View in a popover anchored to its row, with everything it has in the
 * panel: its floating bar controls, its bottom sheet, and a navigation stack of
 * its own. The View runs in its own `ViewScope`, so navigating inside it never
 * moves the panel.
 */
export function ViewPopover({ view, anchor, onClose, closeRequest }: ViewPopoverProps) {
  const store = useStore()
  const dockSide = useSidepanelSide()
  const panelOpen = useSidepanelState() === 'open'
  const { openView } = useViewActions()

  // Held past close so the View stays on screen while the popup fades out.
  const [scope, setScope] = useState<ViewScope | null>(null)
  const [openedView, setOpenedView] = useState(view)
  if (view !== openedView) {
    setOpenedView(view)
    if (view) setScope(createViewScope(view))
  }

  const open = view !== null && panelOpen
  useClaimHostedViewOpen(open)

  useEffect(() => {
    if (view && !panelOpen) onClose()
  }, [view, panelOpen, onClose])

  // Every way out of the popup is a navigation off the View, so each waits on
  // the View's leave guard — moving it into the panel or a window as much as
  // closing it, since the hosted scope and its unsaved edits go either way.
  const requestLeave = useCallback(
    (leave: () => void) => {
      const guard = scope && store.get(scope.navigationGuard)
      if (scope && guard?.confirm && guard.entryKey === topEntry(store.get(scope.history)).key) {
        guard.confirm(leave)
        return
      }
      leave()
    },
    [scope, store],
  )

  // `then` is what the opener of the *next* View is waiting on, so it runs
  // after this one has gone and not at all if its confirmation is cancelled.
  const requestClose = useCallback(
    (then?: () => void) => {
      requestLeave(() => {
        onClose()
        then?.()
      })
    },
    [requestLeave, onClose],
  )

  useEffect(() => {
    if (!closeRequest) return
    closeRequest.current = requestClose
    return () => {
      closeRequest.current = null
    }
  }, [closeRequest, requestClose])

  const readOpenView = useCallback((): { id: ViewId; context: unknown; sourceLabel?: string } | null => {
    if (!scope) return null
    const { id, context, sourceLabel } = topEntry(store.get(scope.history)).view
    return isViewId(id) ? { id, context, sourceLabel } : null
  }, [scope, store])

  const openInPanel = useCallback(() => {
    const current = readOpenView()
    if (!current) return
    requestLeave(() => {
      onClose()
      ;(openView as LooseOpenView)(current.id, current.context, { sourceLabel: current.sourceLabel })
    })
  }, [readOpenView, requestLeave, onClose, openView])

  const openInWindow = useCallback(() => {
    const current = readOpenView()
    if (!current) return
    requestLeave(() => {
      onClose()
      openViewWindow({ id: current.id, context: current.context })
    })
  }, [readOpenView, requestLeave, onClose])

  const finalFocus = useKeyboardFinalFocus(open)

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next) requestClose()
      }}
    >
      <Popover.Portal>
        {/* Opposite the docked edge, like the hover cards, so it floats over the screen rather than off it. */}
        <Popover.Positioner
          anchor={anchor}
          side={oppositeSide(dockSide)}
          align="center"
          sideOffset={dockSide === 'left' ? 12 : 8}
          collisionPadding={16}
        >
          <Popover.Popup
            finalFocus={finalFocus}
            className="flex h-[min(640px,var(--available-height))] w-[400px] flex-col overflow-hidden p-0"
          >
            {scope && (
              <ViewScopeContext.Provider value={scope}>
                <HostedViewHeader
                  onOpenInPanel={openInPanel}
                  onOpenInWindow={openInWindow}
                  // Wrapped: the header's Close is a Button, which would hand
                  // its click event in as the continuation.
                  onClose={() => requestClose()}
                />
                <div className="relative flex min-h-0 flex-1 flex-col">
                  <HostedView />
                  <ViewSheetLayer />
                </div>
                {/* The bar floats above this edge, as it floats above the composer in the panel. */}
                <div className="relative h-1 shrink-0">
                  <SidepanelFloatingBar showsMeetingCluster={false} />
                </div>
              </ViewScopeContext.Provider>
            )}
          </Popover.Popup>
        </Popover.Positioner>
      </Popover.Portal>
    </Popover>
  )
}

/** The scope's top entry, as the router renders it in the panel, minus the swap motion. */
function HostedView() {
  const { topView, topEntryKey } = useViewNavigation()
  const ViewComponent = viewChrome(topView)?.render
  const contextValue = useMemo(
    () => ({ id: topView.id, context: topView.context, entryKey: topEntryKey }),
    [topView, topEntryKey],
  )

  if (!ViewComponent) return null

  return (
    <div className="absolute inset-0" data-view-id={topView.id}>
      <ViewContext.Provider value={contextValue}>
        <ViewComponent key={topEntryKey} />
      </ViewContext.Provider>
    </div>
  )
}

function HostedViewHeader({
  onOpenInPanel,
  onOpenInWindow,
  onClose,
}: {
  onOpenInPanel: () => void
  onOpenInWindow: () => void
  onClose: () => void
}) {
  const { topView } = useViewNavigation()
  const chrome = viewChrome(topView)
  const Icon = chrome?.icon

  return (
    <div className="flex h-11 shrink-0 items-center gap-1 px-2">
      <ViewChromeNav pin={null} />
      <div className="flex min-w-0 flex-1 items-center gap-2 px-1.5">
        {Icon && <Icon aria-hidden="true" className="text-foreground-secondary size-3.5 shrink-0" />}
        <span className="text-foreground type-ui-sm truncate font-medium">{chrome?.trailLabel}</span>
      </div>
      <HeaderButton label="Open in panel" onClick={onOpenInPanel}>
        <Maximize2 />
      </HeaderButton>
      {canPopOutView && (
        <HeaderButton label={POPOUT_LABEL} onClick={onOpenInWindow}>
          <PopoutIcon />
        </HeaderButton>
      )}
      <HeaderButton label="Close" onClick={onClose}>
        <X />
      </HeaderButton>
    </div>
  )
}

function HeaderButton({ label, onClick, children }: { label: string; onClick: () => void; children: JSX.Element }) {
  return (
    <Tooltip>
      <Tooltip.Trigger
        render={
          <Button variant="ghost" shape="square" onClick={onClick} aria-label={label}>
            {children}
          </Button>
        }
      />
      <Tooltip.Content side="bottom">{label}</Tooltip.Content>
    </Tooltip>
  )
}
